import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import type {
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { IconButton } from '@mui/material';
import { MoreVertOutlined as MoreVertOutlinedIcon } from '@mui/icons-material';

import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import type { SyncedTripLeg } from '../../../data/models/reservation.model';
import { TripStatusTodaysTripsBCList } from '../../../data/models/trip-leg-status.model';
import type { TripLegSearchResponseItem } from '../../../data/models/trip-leg-search.model';

import triplegService from '@/services/triplegService';

import UpdateStatusDialog from './UpdateStatusDialog';
import { FindProviderDialog } from '../triplegs/find-provider/FindProviderDialog';
import {
  DataGridPro,
  CellActions,
  CellDualValue,
  ButtonException,
} from '../../tables/DataGridPro';
import { PagedList } from '../../../data/models/paged-list.model';
import { useAppSelector } from '@/hooks/useAppSelector';

import * as lookupSlice from '../../../data/lookup.slice';

interface TodaysTripsTableProps {
  todaysTripLegs: PagedList<TripLegSearchResponseItem>;
  todaysTripLegsLoading: boolean;
  page: number;
  pageSize: number;
  handlePaginationChange: (
    model: GridPaginationModel,
    details: GridCallbackDetails,
  ) => void;
  handleSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => void;
}

const TodaysTripsTable = ({
  todaysTripLegs,
  todaysTripLegsLoading,
  page,
  pageSize,
  handlePaginationChange,
  handleSortModelChange,
}: TodaysTripsTableProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();

  const timezones = useAppSelector(lookupSlice.selectTimeZones);

  const [selectedTripLeg, setSelectedTripLeg] = useState<SyncedTripLeg>();
  const [loadingTripLegData, setLoadingTripLegData] = useState(false);
  const [dialogFindProviderOpen, setDialogFindProviderOpen] = useState(false);
  const [dialogUpdateStatusOpen, setDialogUpdateStatusOpen] = useState(false);

  const handleFindProvider = (
    evt: React.MouseEvent<HTMLElement>,
    trip: TripLegSearchResponseItem,
  ) => {
    evt.stopPropagation();
    getTripLeg(trip.tripLegReferenceID!, () => setDialogFindProviderOpen(true));
  };

  const getTripLeg = (tripLegReferenceID: string, callback: () => void) => {
    setLoadingTripLegData(true);
    triplegService
      .getTripLeg({ tripLegReferenceID })
      .subscribe((tripLeg: SyncedTripLeg) => {
        setSelectedTripLeg(tripLeg);
        if (callback) {
          callback();
        }
        setLoadingTripLegData(false);
      });
  };

  const handleMore = (
    evt: React.MouseEvent<HTMLElement>,
    trip: SyncedTripLeg,
  ) => {
    evt.stopPropagation();
    // TODO: figure out what this is supposed to do (there is a separate ticket for this)
    // console.log('handleMore', evt, trip);
  };

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('todaysTrips.pickup'),
      flex: 0.093,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        // TODO: We need to unify data structures
        // if the object contains a tripLeg property, it's probably from the API
        // if not, it came in from a websocket event
        const tripLeg = params.row;
        let puTime = '';
        if (!tripLeg.isWillCall) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          puTime = m.toFormat('h:mma');
        }
        const primaryValue = tripLeg.isWillCall
          ? t('todaysTrips.willCall')
          : puTime;

        return (
          <CellDualValue
            primaryValue={primaryValue}
            secondaryValue={tripLeg.tripLegReferenceID}
          />
        );
      },
    },
    {
      field: 'pickUpAddress',
      headerName: t('todaysTrips.pickupAddress'),
      flex: 0.242,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const tripLeg = params.row;
        return (
          <CellDualValue
            primaryValue={`${tripLeg.pickUpAddressLine1}`}
            secondaryValue={`${tripLeg.pickUpCity} ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode.substring(0, 5)}`}
          />
        );
      },
    },
    {
      field: 'dropOffAddress',
      headerName: t('todaysTrips.dropoffAddress'),
      flex: 0.272,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const tripLeg = params.row;
        return (
          <CellDualValue
            primaryValue={`${tripLeg.dropOffAddressLine1}`}
            secondaryValue={`${tripLeg.dropOffCity} ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode.substring(0, 5)}`}
          />
        );
      },
    },
    {
      field: 'passenger',
      headerName: t('todaysTrips.member'),
      flex: 0.15,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const passengerFirstName = params.row.passengerFirstName;
        const passengerLastName = params.row.passengerLastName;
        const clientPassengerID = params.row.clientPassengerID;
        return (
          <CellDualValue
            primaryValue={`${passengerFirstName} ${passengerLastName}`}
            secondaryValue={`${clientPassengerID}`}
          />
        );
      },
    },
    {
      field: 'provider',
      headerName: t('todaysTrips.provider'),
      flex: 0.146,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const trip = params.row;
        return (
          <CellDualValue
            primaryValue={trip.providerName || ''}
            secondaryValue=""
          />
        );
      },
    },
    {
      field: 'tripLegStatusName',
      headerName: t('todaysTrips.exception'),
      flex: 0.097,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const trip = params.row;
        return (
          <CellDualValue
            primaryValue={params.row.exceptionValue || ''}
            secondaryValue=""
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 153,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <CellActions>
          {TripStatusTodaysTripsBCList.includes(
            params.row.tripLegStatusBC.toUpperCase(),
          ) &&
            !isSuperReaderUser && (
              <ButtonException
                onClick={(evt) => handleFindProvider(evt, params.row)}
                label={t('todaysTrips.findProvider')}
              />
            )}
          {/* <ButtonException
            onClick={(evt) => handleUpdateStatus(evt, params.row)}
            label={t('todaysTrips.updateStatus')}
          /> */}
          {!isSuperReaderUser && (
            <IconButton
              size="large"
              onClick={(evt) => handleMore(evt, params.row)}>
              <MoreVertOutlinedIcon />
            </IconButton>
          )}
        </CellActions>
      ),
    },
  ];

  const handleRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    navigate(
      `/passengers/${params.row.passengerID}/reservations/${params.row.reservationID}?tripLegID=${params.row.tripLegReferenceID}`,
    );
  };

  const closeFindProviderDialog = () => {
    setDialogFindProviderOpen(false);
  };

  const closeUpdateStatusDialog = () => {
    setDialogUpdateStatusOpen(false);
  };

  return (
    <>
      <DataGridPro
        getRowId={(row) => row.tripLegReferenceID}
        pagination={true}
        paginationModel={{ page: page - 1, pageSize }}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[10, 20, 40, 50]}
        rowCount={todaysTripLegs.itemCount}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        rows={todaysTripLegs.items}
        columns={columns}
        disableColumnSelector={true}
        rowHeight={56}
        disableRowSelectionOnClick={true}
        loading={todaysTripLegsLoading}
      />
      {selectedTripLeg && dialogFindProviderOpen && (
        <FindProviderDialog
          tripLeg={selectedTripLeg}
          open={dialogFindProviderOpen}
          close={closeFindProviderDialog}
        />
      )}

      {selectedTripLeg && dialogUpdateStatusOpen && (
        <UpdateStatusDialog
          title={t('todaysTrips.updateStatus')}
          subTitle={selectedTripLeg.tripLegReferenceID}
          open={dialogUpdateStatusOpen}
          close={closeUpdateStatusDialog}
          tripLeg={selectedTripLeg}
        />
      )}
    </>
  );
};

export { TodaysTripsTable };
